import React, { useState, useEffect } from 'react';
import {
    Alert,
    Box,
    Typography,
    Grid2,
    Card,
    CardContent,
    Chip,
    TextField,
    InputAdornment,
    Button,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Menu,
    MenuItem,
    Tooltip
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    CheckCircle as ExecutedIcon,
    HourglassEmpty as PendingIcon,
    Cancel as CancelledIcon,
    Info as InfoIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    GetApp as ExportIcon,
    Visibility as ViewIcon,
    Timeline as TimelineIcon,
    Close as CloseIcon
} from '@mui/icons-material';

// Check if subgraphs are disabled via environment variable
const SUBGRAPHS_DISABLED = import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true';

// Mock data for demonstration (Phase 2 & 3 features) - ENHANCED with schedule parameters
const mockOperations = [
    {
        id: '0x1234567890abcdef1234567890abcdef12345678',
        target: { id: '0xabcdef1234567890abcdef1234567890abcdef12', name: 'MemerCore' },
        value: '0',
        data: '0xa9059cbb000000000000000000000000742d35cc6634c0532925a3b8d',
        scheduledAt: '1704067200',
        executionTime: '1704153600',
        executedAt: null,
        status: 'SCHEDULED',
        scheduledTransaction: { id: '0xabc123', from: '0x1234...5678', timestamp: '1704067200' },
        executedTransaction: null,
        // ENHANCEMENT: Add schedule parameters for view details
        scheduleParameters: {
            target: '0xabcdef1234567890abcdef1234567890abcdef12',
            value: '0',
            data: '{{fb_1755827905976_cfbd2571ee891cb3}}',
            predecessor: '0x0000000000000000000000000000000000000000000000000000000000000000',
            salt: '0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef12',
            delay: '900', // Use minimum delay from contract
            operationHash: '0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef12',
            executionTime: '2024-01-02T00:00:00.000Z',
            scheduledAt: '2024-01-01T00:00:00.000Z',
            scheduledBy: '0x1234567890123456789012345678901234567890'
        }
    },
    {
        id: '0x2345678901bcdef12345678901bcdef123456789',
        target: { id: '0xbcdef12345678901bcdef12345678901bcdef123', name: 'MemerFeatures' },
        value: '1000000000000000000',
        data: '0x40c10f19000000000000000000000000742d35cc6634c0532925a3b8d',
        scheduledAt: '1704000000',
        executionTime: '1704086400',
        executedAt: '1704086500',
        status: 'EXECUTED',
        scheduledTransaction: { id: '0xdef456', from: '0x2345...6789', timestamp: '1704000000' },
        executedTransaction: { id: '0x789abc', from: '0x3456...7890', timestamp: '1704086500' },
        // ENHANCEMENT: Add schedule parameters for view details
        scheduleParameters: {
            target: '0xbcdef12345678901bcdef12345678901bcdef123',
            value: '1000000000000000000',
            data: '{{fb_1755827968503_c5b4c961cc9aae09}}',
            predecessor: '0x0000000000000000000000000000000000000000000000000000000000000000',
            salt: '0x2345678901bcdef12345678901bcdef12345678901bcdef12345678901bcdef123',
            delay: '900', // Use minimum delay from contract
            operationHash: '0x2345678901bcdef12345678901bcdef12345678901bcdef12345678901bcdef123',
            executionTime: '2024-01-01T12:00:00.000Z',
            scheduledAt: '2024-01-01T00:00:00.000Z',
            scheduledBy: '0x2345678901234567890123456789012345678901'
        }
    },
    {
        id: '0x3456789012cdef123456789012cdef1234567890',
        target: { id: '0xcdef123456789012cdef123456789012cdef1234', name: 'MultiSigWallet' },
        value: '0',
        data: '0x095ea7b3000000000000000000000000742d35cc6634c0532925a3b8d',
        scheduledAt: '1703980000',
        executionTime: '1704066400',
        executedAt: null,
        status: 'EXECUTABLE',
        scheduledTransaction: { id: '0x456def', from: '0x4567...8901', timestamp: '1703980000' },
        executedTransaction: null,
        // ENHANCEMENT: Add schedule parameters for view details
        scheduleParameters: {
            target: '0xcdef123456789012cdef123456789012cdef1234',
            value: '0',
            data: '{{fb_1755815794336_07816ac493ea8d7c}}',
            predecessor: '0x0000000000000000000000000000000000000000000000000000000000000000',
            salt: '0x3456789012cdef123456789012cdef123456789012cdef123456789012cdef1234',
            delay: '86400',
            operationHash: '0x3456789012cdef123456789012cdef123456789012cdef123456789012cdef1234',
            executionTime: '2024-01-01T18:00:00.000Z',
            scheduledAt: '2024-01-01T06:00:00.000Z',
            scheduledBy: '0x3456789012345678901234567890123456789012'
        }
    }
];

// Helper functions
const formatTimestamp = (timestamp: string) => {
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleString();
};

const truncateAddress = (address: string) => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
};

const StatusChip = ({ status }: { status: string }) => {
    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';

    switch (status) {
        case 'SCHEDULED':
            color = 'info';
            break;
        case 'EXECUTABLE':
            color = 'warning';
            break;
        case 'EXECUTED':
            color = 'success';
            break;
        case 'CANCELLED':
            color = 'error';
            break;
    }

    return <Chip label={status} color={color} size="small" />;
};

// Component to display timelock operations
const TimelockOperations: React.FC = () => {
    console.log('TimelockOperations-v2 component rendering...');
    console.log('VITE_DISABLE_SUBGRAPHS:', import.meta.env.VITE_DISABLE_SUBGRAPHS);

    // State for Phase 2 & 3 features
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const [selectedOperation, setSelectedOperation] = useState<any>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
    const [operations, setOperations] = useState(mockOperations);
    const [highlightedTxHash, setHighlightedTxHash] = useState<string | null>(null);
    const [showWorkflowIntegrationAlert, setShowWorkflowIntegrationAlert] = useState(false);
    const [findOperationInput, setFindOperationInput] = useState('');
    const [foundOperation, setFoundOperation] = useState<any>(null);
    const [operationStatus, setOperationStatus] = useState<string>('');

    // Listen for navigation events from WorkflowExecutor
    useEffect(() => {
        const handleNavigationEvent = (event: CustomEvent) => {
            const { tab, action, transactionHash, workflowId, workflowName } = event.detail;

            if (tab === 'timelock-operations' && transactionHash) {
                console.log('🎯 TimelockOperations: Received navigation event:', {
                    action,
                    transactionHash,
                    workflowId,
                    workflowName
                });

                // Highlight the transaction hash
                setHighlightedTxHash(transactionHash);
                setShowWorkflowIntegrationAlert(true);

                // Auto-search for the transaction hash
                setSearchTerm(transactionHash);

                // Auto-dismiss the alert after 5 seconds
                setTimeout(() => {
                    setShowWorkflowIntegrationAlert(false);
                }, 5000);
            }
        };

        const eventListener = (event: Event) => {
            handleNavigationEvent(event as CustomEvent);
        };

        window.addEventListener('navigateToTab', eventListener);

        return () => {
            window.removeEventListener('navigateToTab', eventListener);
        };
    }, []);

    // Early return if subgraphs are disabled - but with enhanced UI
    if (SUBGRAPHS_DISABLED) {
        // Filter operations based on search and status
        const filteredOperations = operations.filter(op => {
            const matchesSearch = searchTerm === '' ||
                op.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                op.target.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                op.target.id.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = statusFilter === 'ALL' || op.status === statusFilter;

            return matchesSearch && matchesStatus;
        });

        // Get operation statistics
        const getOperationStats = () => {
            const scheduled = operations.filter(op => op.status === 'SCHEDULED').length;
            const executed = operations.filter(op => op.status === 'EXECUTED').length;
            const cancelled = operations.filter(op => op.status === 'CANCELLED').length;
            const executable = operations.filter(op => op.status === 'EXECUTABLE').length;

            return { total: operations.length, scheduled, executed, cancelled, executable };
        };

        const stats = getOperationStats();

        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Timelock Operations (Enhanced Demo Mode)
                </Typography>

                {/* Workflow Integration Alert */}
                {showWorkflowIntegrationAlert && highlightedTxHash && (
                    <Alert
                        severity="success"
                        sx={{ mb: 3 }}
                        onClose={() => setShowWorkflowIntegrationAlert(false)}
                    >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            🎯 Workflow Integration Active
                        </Typography>
                        <Typography variant="body2">
                            Navigated from completed workflow. Searching for transaction hash:
                            <strong style={{ fontFamily: 'monospace', marginLeft: '8px' }}>
                                {highlightedTxHash.substring(0, 10)}...{highlightedTxHash.substring(highlightedTxHash.length - 8)}
                            </strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            This transaction represents a MultiSig execution that scheduled operations in the timelock.
                        </Typography>
                    </Alert>
                )}

                {/* Summary Statistics */}
                <Grid2 container spacing={3} sx={{ mb: 3 }}>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <TimelineIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" fontWeight="bold">
                                    {stats.total}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <PendingIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" fontWeight="bold" color="info.main">
                                    {stats.scheduled}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Scheduled
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <ScheduleIcon color="warning" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" fontWeight="bold" color="warning.main">
                                    {stats.executable}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Executable
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <ExecutedIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" fontWeight="bold" color="success.main">
                                    {stats.executed}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Executed
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <CancelledIcon color="error" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" fontWeight="bold" color="error.main">
                                    {stats.cancelled}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Cancelled
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid2>
                </Grid2>

                {/* Find Operation Section */}
                <Card sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Find Operation
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <TextField
                            placeholder="Enter transaction hash or operation ID..."
                            value={findOperationInput}
                            onChange={(e) => setFindOperationInput(e.target.value)}
                            sx={{ flexGrow: 1, minWidth: 300 }}
                            size="small"
                        />
                        <Button
                            variant="contained"
                            onClick={() => {
                                const found = operations.find(op =>
                                    op.id.toLowerCase() === findOperationInput.toLowerCase() ||
                                    op.scheduledTransaction.id.toLowerCase() === findOperationInput.toLowerCase() ||
                                    (op.executedTransaction && op.executedTransaction.id.toLowerCase() === findOperationInput.toLowerCase())
                                );

                                if (found) {
                                    setFoundOperation(found);
                                    setOperationStatus(`Found operation ID: ${found.id} Status: Exists Pending: ${found.status !== 'EXECUTED'} Ready: ${found.status === 'EXECUTABLE'}`);
                                } else {
                                    setFoundOperation(null);
                                    setOperationStatus('Operation not found');
                                }
                            }}
                        >
                            Find Operation
                        </Button>
                    </Box>

                    {/* Active Operations Display */}
                    {foundOperation && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Active Operations
                            </Typography>
                            <Card variant="outlined" sx={{ p: 2 }}>
                                <Grid2 container spacing={2}>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Status
                                        </Typography>
                                        <StatusChip status={foundOperation.status} />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Operation ID
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                            {foundOperation.id}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Target
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {foundOperation.target.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                            {foundOperation.target.id}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Execution Time
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatTimestamp(foundOperation.executionTime)}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Time Remaining
                                        </Typography>
                                        <Typography variant="body2">
                                            {foundOperation.status === 'EXECUTED' ? 'Completed' :
                                                foundOperation.status === 'EXECUTABLE' ? 'Ready to Execute' :
                                                    `${Math.max(0, Math.round((parseInt(foundOperation.executionTime) - Date.now() / 1000) / 3600))} hours`}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ViewIcon />}
                                            onClick={() => {
                                                console.log('🔍 View button clicked for operation:', foundOperation?.id);
                                                console.log('Setting selectedOperation:', foundOperation);
                                                console.log('Opening dialog...');
                                                setSelectedOperation(foundOperation);
                                                setDetailDialogOpen(true);
                                                console.log('Dialog state should now be true');
                                            }}
                                        >
                                            View Full Details
                                        </Button>
                                    </Grid2>
                                </Grid2>
                            </Card>
                        </Box>
                    )}

                    {/* Transaction Status */}
                    {operationStatus && (
                        <Alert severity={foundOperation ? 'success' : 'info'} sx={{ mt: 2 }}>
                            {operationStatus}
                        </Alert>
                    )}
                </Card>

                {/* Search and Filter Controls */}
                <Card sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            placeholder="Search operations by ID, target, or address..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ flexGrow: 1, minWidth: 300 }}
                            size="small"
                        />

                        <Button
                            variant="outlined"
                            startIcon={<FilterIcon />}
                            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                            size="small"
                        >
                            Filter: {statusFilter}
                        </Button>

                        <Tooltip title="Export operations data">
                            <Button
                                variant="outlined"
                                startIcon={<ExportIcon />}
                                onClick={() => {
                                    const dataStr = JSON.stringify(filteredOperations, null, 2);
                                    const dataBlob = new Blob([dataStr], { type: 'application/json' });
                                    const url = URL.createObjectURL(dataBlob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = 'timelock-operations.json';
                                    link.click();
                                }}
                                size="small"
                            >
                                Export
                            </Button>
                        </Tooltip>
                    </Box>
                </Card>

                {/* Filter Menu */}
                <Menu
                    anchorEl={filterMenuAnchor}
                    open={Boolean(filterMenuAnchor)}
                    onClose={() => setFilterMenuAnchor(null)}
                >
                    {['ALL', 'SCHEDULED', 'EXECUTABLE', 'EXECUTED', 'CANCELLED'].map((status) => (
                        <MenuItem
                            key={status}
                            onClick={() => {
                                setStatusFilter(status);
                                setFilterMenuAnchor(null);
                            }}
                            selected={statusFilter === status}
                        >
                            {status}
                        </MenuItem>
                    ))}
                </Menu>

                {/* Operations Grid */}
                <Grid2 container spacing={2}>
                    {filteredOperations.map((operation) => (
                        <Grid2 size={{ xs: 12, md: 6, lg: 4 }} key={operation.id}>
                            <Card
                                variant="outlined"
                                sx={{
                                    height: '100%',
                                    borderLeft: '4px solid',
                                    borderLeftColor:
                                        operation.status === 'EXECUTED' ? 'success.main' :
                                            operation.status === 'CANCELLED' ? 'error.main' :
                                                operation.status === 'EXECUTABLE' ? 'warning.main' : 'info.main',
                                    '&:hover': {
                                        boxShadow: 2,
                                        cursor: 'pointer'
                                    }
                                }}
                                onClick={() => {
                                    setSelectedOperation(operation);
                                    setDetailDialogOpen(true);
                                }}
                            >
                                <CardContent>
                                    {/* Operation Header */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {operation.status === 'EXECUTED' ? (
                                                <ExecutedIcon color="success" sx={{ mr: 1 }} />
                                            ) : operation.status === 'CANCELLED' ? (
                                                <CancelledIcon color="error" sx={{ mr: 1 }} />
                                            ) : operation.status === 'EXECUTABLE' ? (
                                                <ScheduleIcon color="warning" sx={{ mr: 1 }} />
                                            ) : (
                                                <PendingIcon color="info" sx={{ mr: 1 }} />
                                            )}
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {truncateAddress(operation.id)}
                                            </Typography>
                                        </Box>
                                        <StatusChip status={operation.status} />
                                    </Box>

                                    <Divider sx={{ mb: 2 }} />

                                    {/* Operation Details */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Target
                                            </Typography>
                                            <Typography variant="body2">
                                                {operation.target.name || truncateAddress(operation.target.id)}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Value
                                            </Typography>
                                            <Typography variant="body2">
                                                {operation.value === '0' ? '0 ETH' : `${parseInt(operation.value) / 1e18} ETH`}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Scheduled At
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatTimestamp(operation.scheduledAt)}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Execution Time
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatTimestamp(operation.executionTime)}
                                            </Typography>
                                        </Box>

                                        {operation.executedAt && (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Executed At
                                                </Typography>
                                                <Typography variant="body2">
                                                    {formatTimestamp(operation.executedAt)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Action Buttons */}
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        <Button
                                            size="small"
                                            startIcon={<ViewIcon />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedOperation(operation);
                                                setDetailDialogOpen(true);
                                            }}
                                        >
                                            View Details
                                        </Button>
                                        {operation.status === 'EXECUTABLE' && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="warning"
                                                startIcon={<ScheduleIcon />}
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    // Create schedule data from the operation
                                                    const scheduleData = {
                                                        target: operation.target.id,
                                                        value: operation.value,
                                                        data: operation.data,
                                                        predecessor: '0x0000000000000000000000000000000000000000000000000000000000000000', // Default predecessor
                                                        salt: operation.id, // Use operation ID as salt reference
                                                        delay: '0' // Execute operations have no additional delay
                                                    };

                                                    console.log('🚀 TimelockOperations: Starting execute workflow for operation:', {
                                                        operationId: operation.id,
                                                        scheduleData
                                                    });

                                                    // Emit navigation event to start execute workflow
                                                    const navigationEvent = new CustomEvent('startExecuteWorkflow', {
                                                        detail: {
                                                            scheduleData,
                                                            workflowName: 'timelock_to_multisig',
                                                            transactionHash: operation.scheduledTransaction.id,
                                                            operationId: operation.id,
                                                            source: 'timelock-operations'
                                                        }
                                                    });
                                                    window.dispatchEvent(navigationEvent);

                                                    // Also emit a tab navigation event
                                                    const tabNavigationEvent = new CustomEvent('navigateToTab', {
                                                        detail: {
                                                            tab: 'workflows',
                                                            action: 'start-execute-workflow',
                                                            scheduleData,
                                                            source: 'timelock-operations'
                                                        }
                                                    });
                                                    window.dispatchEvent(tabNavigationEvent);
                                                }}
                                            >
                                                Execute Operation
                                            </Button>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid2>
                    ))}
                </Grid2>

                {/* No Results */}
                {filteredOperations.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        No operations found matching your search criteria.
                    </Alert>
                )}

                {/* Demo Mode Notice */}
                <Alert severity="warning" sx={{ mt: 3 }}>
                    <Typography variant="h6">Enhanced Demo Mode</Typography>
                    <Typography variant="body2">
                        Subgraphs are disabled (VITE_DISABLE_SUBGRAPHS=true), but this component now includes:
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
                        <li><strong>Phase 2 Features:</strong> Enhanced UI with search, filtering, and responsive card layout</li>
                        <li><strong>Phase 3 Features:</strong> Operation detail modals, export functionality, and interactive elements</li>
                        <li><strong>Mock Data:</strong> Demonstrating the full UI with sample timelock operations</li>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        When subgraphs are re-enabled, this enhanced UI will work with real GraphQL data.
                    </Typography>
                </Alert>

                {/* Operation Detail Dialog */}
                <Dialog
                    open={detailDialogOpen}
                    onClose={() => setDetailDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">Operation Details</Typography>
                            <IconButton onClick={() => setDetailDialogOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {selectedOperation && (
                            <Box sx={{ pt: 1 }}>
                                <Grid2 container spacing={2}>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Operation ID
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 2 }}>
                                            {selectedOperation.id}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Status
                                        </Typography>
                                        <Box sx={{ mb: 2 }}>
                                            <StatusChip status={selectedOperation.status} />
                                        </Box>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Target Contract
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                            {selectedOperation.target.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                            {selectedOperation.target.id}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Value
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                            {selectedOperation.value === '0' ? '0 ETH' : `${parseInt(selectedOperation.value) / 1e18} ETH`}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Transaction Data
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 2 }}>
                                            {selectedOperation.data}
                                        </Typography>
                                    </Grid2>

                                    {/* ENHANCEMENT: Show schedule parameters if available */}
                                    {selectedOperation.scheduleParameters && (
                                        <>
                                            <Grid2 size={{ xs: 12 }}>
                                                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                                                    📋 Schedule Function Parameters
                                                </Typography>
                                            </Grid2>

                                            <Grid2 size={{ xs: 12, md: 6 }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Target Contract
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                                                    {selectedOperation.scheduleParameters.target}
                                                </Typography>
                                            </Grid2>

                                            <Grid2 size={{ xs: 12, md: 6 }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Value (ETH)
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    {selectedOperation.scheduleParameters.value === '0' ? '0 ETH' : `${parseInt(selectedOperation.scheduleParameters.value) / 1e18} ETH`}
                                                </Typography>
                                            </Grid2>

                                            <Grid2 size={{ xs: 12 }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Data (KV Reference to Original Function)
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 1 }}>
                                                    {selectedOperation.scheduleParameters.data}
                                                </Typography>
                                            </Grid2>

                                            <Grid2 size={{ xs: 12, md: 6 }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Predecessor
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                                                    {selectedOperation.scheduleParameters.predecessor === '0x0000000000000000000000000000000000000000000000000000000000000000'
                                                        ? '0x0000...0000 (no dependency)'
                                                        : selectedOperation.scheduleParameters.predecessor}
                                                </Typography>
                                            </Grid2>

                                            <Grid2 size={{ xs: 12, md: 6 }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Salt
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 1 }}>
                                                    {selectedOperation.scheduleParameters.salt}
                                                </Typography>
                                            </Grid2>

                                            <Grid2 size={{ xs: 12, md: 6 }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Delay
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    {selectedOperation.scheduleParameters.delay} seconds ({Math.round(parseInt(selectedOperation.scheduleParameters.delay) / 3600)} hours)
                                                </Typography>
                                            </Grid2>

                                            {selectedOperation.scheduleParameters.operationHash && (
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Operation Hash
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 1 }}>
                                                        {selectedOperation.scheduleParameters.operationHash}
                                                    </Typography>
                                                </Grid2>
                                            )}

                                            {selectedOperation.scheduleParameters.executionTime && (
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Execution Time
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        {new Date(selectedOperation.scheduleParameters.executionTime).toLocaleString()}
                                                    </Typography>
                                                </Grid2>
                                            )}
                                        </>
                                    )}
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Scheduled By
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                            {truncateAddress(selectedOperation.scheduledTransaction.from)}
                                        </Typography>
                                    </Grid2>
                                    {selectedOperation.executedTransaction && (
                                        <Grid2 size={{ xs: 12, md: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Executed By
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {truncateAddress(selectedOperation.executedTransaction.from)}
                                            </Typography>
                                        </Grid2>
                                    )}
                                </Grid2>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDetailDialogOpen(false)}>
                            Close
                        </Button>
                        {selectedOperation && (
                            <Button
                                variant="contained"
                                startIcon={<TimelineIcon />}
                                onClick={() => {
                                    // Phase 3: Transaction chain tracing
                                    console.log('Tracing transaction chain for:', selectedOperation.id);
                                    // This would integrate with your existing transaction tracing functionality
                                }}
                            >
                                Trace Chain
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    // This code would only run if subgraphs are enabled (Phase 1 - future implementation)
    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Timelock Operations (Subgraph Data)
            </Typography>
            <Alert severity="info">
                Subgraphs are enabled but GraphQL integration is not yet implemented.
                The enhanced UI features are ready for when GraphQL data becomes available.
            </Alert>
        </Box>
    );
};

export default TimelockOperations;
